"use client";

import { c as _c } from "react/compiler-runtime";
import { ExternalLink } from "lucide-react";
import { DateTime } from "luxon";
import { ENTP_REFERRAL_TEL_ENC_ASSIGNMENT_DEFAULT } from "@/configuration/groups/entpartners/configuration";
import { ENTP_CHICAGO_ECW_LOGIN_URL, ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE, ENTP_SUBOTO_REFERRAL_TEL_ENC_ASSIGNEE, getEntpProviderDisplayNameFromEhrId } from "@/configuration/groups/entpartners/referral-instructions";
import { ENTS_REFERRAL_ASSIGNEE } from "@/configuration/groups/ents/configuration";
import { buildEntsTelEncMessage, ENTS_TEL_ENC_REASON } from "@/configuration/groups/ents/tel-enc-message";
import { MENT_ECW_LOGIN_URL, MENT_REFERRAL_FACILITY, MENT_REFERRAL_FRONT_OFFICE_ASSIGNEE } from "@/configuration/groups/ment/configuration";
import { ENTP_SCI_ECW_LOGIN_URL, ENTP_SCI_NCH_NEUROSCIENCES_SYNC, isEntpSciNchNeurosciencesSyncReferral } from "@/configuration/groups/sci/referral-instructions";
import { SIENT_REFERRAL_ASSIGNEE } from "@/configuration/groups/sient/configuration";
import { RecommendationType } from "@/shared";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { SchedulingRecommendationVerification } from "@/src/components/comms/referral-scheduling-recommendation";
import { SchedulingEligibility } from "@/src/components/comms/scheduling-eligibility";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/src/components/shadcn/ui/table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { ValerieCard } from "@/src/components/valerie-card";
import { useReferralSchedulingRecommendation, usePatientAppointments } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { formatDateForFilename, formatDateTimeWithTimezoneForDisplay } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";
import { getPersonName } from "@/src/models/person";
import { isRecommendedForScheduling } from "@/src/models/scheduling-recommendation";
interface EntPartnersReferralInstructionsProps {
  referral: Referral;
  schedulingVerificationTaskEnabled?: boolean;
}
function DefaultENTPReferralMessage(t0) {
  const $ = _c(45);
  const {
    referral,
    groupName,
    assignedTo: t1,
    customReferralMessage: customReferralMessageOverride,
    ecwUrl: t2,
    showSchedulingRecommendation: t3,
    schedulingVerificationTaskEnabled: t4
  } = t0;
  const assignedTo = t1 === undefined ? ENTP_REFERRAL_TEL_ENC_ASSIGNMENT_DEFAULT : t1;
  const ecwUrl = t2 === undefined ? ENTP_CHICAGO_ECW_LOGIN_URL : t2;
  const showSchedulingRecommendation = t3 === undefined ? false : t3;
  const schedulingVerificationTaskEnabled = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== referral.dateOfReferral) {
    t5 = formatDateForFilename(referral.dateOfReferral);
    $[0] = referral.dateOfReferral;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const formattedDateOfReferral = t5;
  let t6;
  if ($[2] !== referral.receivingProvider) {
    t6 = referral.receivingProvider?.familyName ? getPersonName(referral.receivingProvider) : "ENT";
    $[2] = referral.receivingProvider;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const providerName = t6;
  const customReferralMessage = customReferralMessageOverride ?? `Please schedule this patient. Referred to ${providerName} for ${referral.reasonForReferral || ""}`;
  const patient = referral.patient;
  const {
    appointments,
    isPending: isAppointmentsPending
  } = usePatientAppointments(patient?.id);
  const {
    recommendation: referralSchedulingRecommendation,
    isPending: isReferralSchedulingRecommendationPending
  } = useReferralSchedulingRecommendation(referral.id, true);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = DateTime.now().setZone(ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE).startOf("day");
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const startOfDay = t7;
  let t10;
  let t11;
  let t12;
  let t13;
  let t8;
  let t9;
  if ($[5] !== appointments || $[6] !== assignedTo || $[7] !== customReferralMessage || $[8] !== ecwUrl || $[9] !== formattedDateOfReferral || $[10] !== groupName || $[11] !== isAppointmentsPending || $[12] !== isReferralSchedulingRecommendationPending || $[13] !== patient?.displayId || $[14] !== referral.receivingProvider || $[15] !== referralSchedulingRecommendation) {
    const upcomingAppointments = appointments?.filter(appointment => {
      const appointmentDate = DateTime.fromISO(appointment.appointmentDatetime || "").setZone(ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE);
      return appointmentDate.isValid && appointmentDate >= startOfDay && appointment.ehrType && appointment.ehrType !== "new_patient_checkin";
    });
    const renderSchedulingMessage = () => {
      const telEncInstruction = <>Create a tel enc to <CopyableContent text={assignedTo} alwaysShow={true} /> with the following message:{" "}<CopyableContent text={customReferralMessage} alwaysShow={true} /></>;
      if (isAppointmentsPending) {
        return <span className="text-xs text-muted-foreground">Checking for upcoming appointments...</span>;
      }
      if (upcomingAppointments?.length) {
        return <div className="flex flex-col gap-2 text-muted-foreground"><p>3. Scheduling the patient may not be needed. Upcoming appointment{upcomingAppointments.length > 1 ? "s" : ""} found:</p><Table className="text-xs"><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Provider</TableHead><TableHead>Type</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader><TableBody>{upcomingAppointments.map(appointment_0 => <TableRow key={appointment_0.id}><TableCell><a href={`/patients/${patient?.displayId}/appointments`} className="flex items-center gap-1 hover:underline">{formatDateTimeWithTimezoneForDisplay(appointment_0.appointmentDatetime, ENTP_REFERRAL_APPOINTMENT_DISPLAY_TIMEZONE, false)}<ExternalLink className="h-3 w-3" aria-hidden="true" /></a></TableCell><TableCell>{getEntpProviderDisplayNameFromEhrId(appointment_0.providerEhrId)}</TableCell><TableCell>{appointment_0.ehrType}</TableCell><TableCell>{appointment_0.ehrReason || "Unknown Reason"}</TableCell></TableRow>)}</TableBody></Table><p><span className="italic">If the appointment isn't valid:</span> {telEncInstruction}</p></div>;
      }
      if (isReferralSchedulingRecommendationPending) {
        return <span className="text-xs text-muted-foreground">Checking for eligibility...</span>;
      }
      const isEligible = isRecommendedForScheduling(referralSchedulingRecommendation, RecommendationType.NEW_PATIENT) || isRecommendedForScheduling(referralSchedulingRecommendation, RecommendationType.ESTABLISHED_PATIENT);
      const schedulingTitlePrefix = isEligible ? "3. Patient is eligible, send SMS outreach" : "3. Patient is not eligible, send tel enc message";
      return <div className="relative text-muted-foreground"><div className="flex items-center gap-2"><p>{schedulingTitlePrefix}</p><div className="inline-block relative top-[3px]"><SchedulingEligibility agenticRecommendation={referralSchedulingRecommendation?.agenticRecommendation} receivingProvider={referral.receivingProvider} /></div></div>{isEligible ? <div className="mt-1"><p>Confirm in eCW whether the patient already has a matching upcoming scheduled appointment. If not, click on the schedule link above to schedule a new appointment.</p><p className="mt-2"><span className="font-semibold">If needed:</span> <br /> {telEncInstruction}</p></div> : <p className="mt-1">{telEncInstruction}</p>}</div>;
    };
    t12 = "flex flex-col gap-1";
    if ($[22] !== groupName) {
      t13 = <p className="font-medium text-center mb-4">{groupName} Referral Instructions:</p>;
      $[22] = groupName;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    t8 = "text-muted-foreground flex flex-col gap-2";
    let t14;
    if ($[24] !== ecwUrl) {
      t14 = ecwUrl ? <>{" via:"}<a href={ecwUrl} target="_blank" className="hover:bg-accent rounded p-1">Link</a></> : null;
      $[24] = ecwUrl;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    if ($[26] !== t14) {
      t9 = <p>1. Create or Update the patient in eCW{t14}</p>;
      $[26] = t14;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    const t15 = `${formattedDateOfReferral} Referral`;
    if ($[28] !== t15) {
      t10 = <p>2. Upload the file in Patient Docs, under the Referrals folder. Mark as reviewed. Name the file{" "}<CopyableContent text={t15} /></p>;
      $[28] = t15;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    t11 = renderSchedulingMessage();
    $[5] = appointments;
    $[6] = assignedTo;
    $[7] = customReferralMessage;
    $[8] = ecwUrl;
    $[9] = formattedDateOfReferral;
    $[10] = groupName;
    $[11] = isAppointmentsPending;
    $[12] = isReferralSchedulingRecommendationPending;
    $[13] = patient?.displayId;
    $[14] = referral.receivingProvider;
    $[15] = referralSchedulingRecommendation;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t8 || $[33] !== t9) {
    t14 = <div className={t8}>{t9}{t10}{t11}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t8;
    $[33] = t9;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== referral.id || $[36] !== referralSchedulingRecommendation || $[37] !== schedulingVerificationTaskEnabled || $[38] !== showSchedulingRecommendation) {
    t15 = showSchedulingRecommendation && !schedulingVerificationTaskEnabled && <SchedulingRecommendationVerification referralId={referral.id} recommendation={referralSchedulingRecommendation} />;
    $[35] = referral.id;
    $[36] = referralSchedulingRecommendation;
    $[37] = schedulingVerificationTaskEnabled;
    $[38] = showSchedulingRecommendation;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15) {
    t16 = <div className={t12}>{t13}{t14}{t15}</div>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}
function EntPartnersReferralCards(t0) {
  const $ = _c(15);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.patient) {
    t1 = <PatientCard patient={referral.patient} />;
    $[0] = referral.patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== referral) {
    t2 = <ReferralProviderCards referral={referral} />;
    t3 = <ReferralCard referral={referral} isEditable={true} />;
    $[2] = referral;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== referral.primaryCoverage) {
    t4 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[5] = referral.primaryCoverage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== referral.secondaryCoverage) {
    t5 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[7] = referral.secondaryCoverage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
export function EntPartnersChicagoReferralInstructions(t0) {
  const $ = _c(8);
  const {
    referral,
    schedulingVerificationTaskEnabled
  } = t0;
  let t1;
  if ($[0] !== referral || $[1] !== schedulingVerificationTaskEnabled) {
    t1 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><DefaultENTPReferralMessage referral={referral} groupName="ENT Partners" showSchedulingRecommendation={true} schedulingVerificationTaskEnabled={schedulingVerificationTaskEnabled} /></div></EhrInstructionsCard>;
    $[0] = referral;
    $[1] = schedulingVerificationTaskEnabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== referral) {
    t2 = <EntPartnersReferralCards referral={referral} />;
    $[3] = referral;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function EntsReferralInstructions(t0) {
  const $ = _c(22);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.dateOfReferral) {
    t1 = formatDateForFilename(referral.dateOfReferral);
    $[0] = referral.dateOfReferral;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedDateOfReferral = t1;
  const documentLabel = `${formattedDateOfReferral} Referral to ${referral.serviceLine || "ENT"}`;
  let t2;
  if ($[2] !== referral) {
    t2 = buildEntsTelEncMessage(referral);
    $[2] = referral;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const telEncMessage = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p>1. Create or update the patient in eCW.</p>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== documentLabel) {
    t4 = <CopyableContent text={documentLabel} />;
    $[5] = documentLabel;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CopyableContent text={ENTS_REFERRAL_ASSIGNEE} alwaysShow={true} />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = <p>2. Upload the referral in Patient Docs, under the Referral folder. Name the file{" "}{t4} and assign it to{" "}{t5}. Mark it reviewed.</p>;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CopyableContent text={ENTS_REFERRAL_ASSIGNEE} alwaysShow={true} />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <CopyableContent text={ENTS_TEL_ENC_REASON} />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== telEncMessage) {
    t9 = <p>3. Create a telephone encounter assigned to {t7}{" "}with the reason {t8} and message{" "}<CopyableContent text={telEncMessage} />.</p>;
    $[12] = telEncMessage;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t9) {
    t10 = <ValerieCard title="ENTS Referral Instructions" className="border-ehr/20 bg-ehr/5 dark:bg-ehr/20"><div className="text-sm text-muted-foreground flex flex-col gap-2">{t3}{t6}{t9}</div></ValerieCard>;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== referral) {
    t11 = <EntPartnersReferralCards referral={referral} />;
    $[17] = referral;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}
export function SientReferralInstructions(t0) {
  const $ = _c(12);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.dateOfReferral) {
    t1 = formatDateForFilename(referral.dateOfReferral);
    $[0] = referral.dateOfReferral;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedDateOfReferral = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="font-medium text-center mb-4">SIENT Referral Instructions:</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p>1. Create or Update the patient in eCW</p>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CopyableContent text={SIENT_REFERRAL_ASSIGNEE} alwaysShow={true} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `${formattedDateOfReferral} Referral`;
  let t6;
  if ($[5] !== t5) {
    t6 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><div className="flex flex-col gap-1">{t2}<div className="text-muted-foreground flex flex-col gap-2">{t3}<p>2. Upload the file in Patient Docs, under the Referral Notes folder, assigned to{" "}{t4}. Name the file{" "}<CopyableContent text={t5} />. Do NOT mark reviewed.</p></div></div></div></EhrInstructionsCard>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== referral) {
    t7 = <EntPartnersReferralCards referral={referral} />;
    $[7] = referral;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t6}{t7}</div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
}
export function EntPartnersSubOtoReferralInstructions(t0) {
  const $ = _c(2);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral) {
    t1 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto"><EhrInstructionsCard><div className="text-sm text-muted-foreground"><DefaultENTPReferralMessage referral={referral} groupName="ENT Partners SubOto" assignedTo={ENTP_SUBOTO_REFERRAL_TEL_ENC_ASSIGNEE} /></div></EhrInstructionsCard><EntPartnersReferralCards referral={referral} /></div>;
    $[0] = referral;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function EntPartnersSCIReferralInstructions(t0) {
  const $ = _c(19);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.dateOfReferral) {
    t1 = formatDateForFilename(referral.dateOfReferral);
    $[0] = referral.dateOfReferral;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedDateOfReferral = t1;
  const serviceLine = referral.serviceLine || "no service line";
  const fileName = `${formattedDateOfReferral} ${serviceLine} Referral`;
  let t2;
  if ($[2] !== referral) {
    t2 = isEntpSciNchNeurosciencesSyncReferral(referral);
    $[2] = referral;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isNchNeurosciencesSync = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="font-medium mb-4">ENT Partners SCI Referral Instructions:</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p>1. Create or Update the patient in eCW:{" "}<a href={ENTP_SCI_ECW_LOGIN_URL} target="_blank">{ENTP_SCI_ECW_LOGIN_URL}</a></p>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== fileName || $[7] !== isNchNeurosciencesSync) {
    t5 = <p>2. Attach the file in Patient Docs, under the Referrals folder.{" "}{isNchNeurosciencesSync ? <>Name the file <CopyableContent text={fileName} />. <span className="font-semibold">DO NOT</span>{" "}mark the file as reviewed.</> : <>Mark as reviewed. Name the file <CopyableContent text={fileName} /></>}</p>;
    $[6] = fileName;
    $[7] = isNchNeurosciencesSync;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isNchNeurosciencesSync) {
    t6 = isNchNeurosciencesSync && <p>3. Assign the file to{" "}<CopyableContent text={ENTP_SCI_NCH_NEUROSCIENCES_SYNC.assignee} alwaysShow={true} /></p>;
    $[9] = isNchNeurosciencesSync;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><div className="flex flex-col gap-2">{t3}{" "}<div className="text-muted-foreground">{t4}{t5}{t6}</div></div></div></EhrInstructionsCard>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== referral) {
    t8 = <EntPartnersReferralCards referral={referral} />;
    $[14] = referral;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t7}{t8}</div>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
export function EntPartnersMentReferralInstructions(t0) {
  const $ = _c(23);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.dateOfReferral) {
    t1 = formatDateForFilename(referral.dateOfReferral);
    $[0] = referral.dateOfReferral;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedDateOfReferral = t1;
  let t2;
  if ($[2] !== referral.receivingProvider) {
    t2 = referral.receivingProvider?.familyName ? getPersonName(referral.receivingProvider) : "ENT";
    $[2] = referral.receivingProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const providerName = t2;
  const teMessage = `Please schedule this patient. Referred to ${providerName} for ${referral.reasonForReferral || "diagnosis"}.`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="font-medium mb-4">Maryland ENT Referral Instructions:</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p>1. Create or Update the patient in eCW:{" "}<a href={MENT_ECW_LOGIN_URL} target="_blank" rel="noopener noreferrer" className="hover:bg-accent rounded p-1">Link</a></p>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CopyableContent text={MENT_REFERRAL_FRONT_OFFICE_ASSIGNEE} alwaysShow={true} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `${formattedDateOfReferral} ENT Referral`;
  let t7;
  if ($[7] !== t6) {
    t7 = <p>2. Attach the file in Patient Docs, under the Referrals folder. Assign to{" "}{t5}. Name the file{" "}<CopyableContent text={t6} /></p>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <CopyableContent text={MENT_REFERRAL_FRONT_OFFICE_ASSIGNEE} alwaysShow={true} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== teMessage) {
    t9 = <CopyableContent text={teMessage} alwaysShow={true} />;
    $[10] = teMessage;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CopyableContent text={MENT_REFERRAL_FACILITY} alwaysShow={true} />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t9) {
    t11 = <p>3. Create a telephone encounter assigned to{" "}{t8}:{" "}{t9} Facility:{" "}{t10}</p>;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t7) {
    t12 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><div className="flex flex-col gap-2">{t3}{" "}<div className="text-muted-foreground">{t4}{t7}{t11}</div></div></div></EhrInstructionsCard>;
    $[15] = t11;
    $[16] = t7;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== referral) {
    t13 = <EntPartnersReferralCards referral={referral} />;
    $[18] = referral;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t12 || $[21] !== t13) {
    t14 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t12}{t13}</div>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}